
import { Client } from "../types";

interface PrintableClientsListProps {
  clients: Client[];
  filterLabel: string;
  generatedAt: string;
  stats: {
    total: number;
    withEvents: number;
    totalRevenue: number;
    totalBalance: number;
  };
  businessInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    logoUrl?: string;
  };
}

function fmt(n: number) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

export default function PrintableClientsList({
  clients,
  filterLabel,
  generatedAt,
  stats,
  businessInfo,
}: PrintableClientsListProps) {
  return (
    <div className="print-only" style={{ display: "none" }}>
      <style>{`
        @media print {
          body > *:not(.print-only) { display: none !important; }
          .print-only { display: block !important; font-family: 'Segoe UI', Arial, sans-serif; color: #1c1917; padding: 24px; }
          .print-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 20px; border-bottom: 2px solid #e7e5e4; padding-bottom: 14px; }
          .biz-name { font-size: 20px; font-weight: 800; color: #1c1917; }
          .biz-sub { font-size: 11px; color: #78716c; margin-top: 3px; }
          .report-title { font-size: 18px; font-weight: 700; text-align: right; color: #1c1917; }
          .report-meta { font-size: 10px; color: #78716c; text-align: right; margin-top: 3px; }
          .stats-row { display: flex; gap: 12px; margin-bottom: 18px; }
          .stat-box { flex: 1; border: 1px solid #e7e5e4; border-radius: 8px; padding: 10px 12px; }
          .stat-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #78716c; font-weight: 600; margin-bottom: 3px; }
          .stat-value { font-size: 15px; font-weight: 800; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px; }
          thead tr { background: #f5f5f4; }
          th { text-align: left; padding: 8px 10px; font-weight: 700; font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: #78716c; border-bottom: 1px solid #d6d3d1; }
          th.right { text-align: right; }
          td { padding: 7px 10px; border-bottom: 1px solid #f5f5f4; color: #292524; }
          td.right { text-align: right; font-variant-numeric: tabular-nums; }
          tr.total-row td { background: #f5f5f4; font-weight: 800; border-top: 2px solid #d6d3d1; }
          .cleared { color: #16a34a; }
          .outstanding { color: #b45309; font-weight: 700; }
          .footer { margin-top: 24px; font-size: 9px; color: #a8a29e; text-align: center; border-top: 1px solid #e7e5e4; padding-top: 10px; }
          @page { margin: 16mm 14mm; size: A4 landscape; }
        }
      `}</style>

      {/* Header */}
      <div className="print-header">
        <div>
          {businessInfo?.logoUrl && (
            <img src={businessInfo.logoUrl} alt="logo" style={{ height: 36, marginBottom: 6 }} />
          )}
          <div className="biz-name">{businessInfo?.name || "Business"}</div>
          {businessInfo?.address && <div className="biz-sub">{businessInfo.address}</div>}
          {(businessInfo?.phone || businessInfo?.email) && (
            <div className="biz-sub">
              {[businessInfo.phone, businessInfo.email].filter(Boolean).join("  ·  ")}
            </div>
          )}
        </div>
        <div>
          <div className="report-title">Clients Report</div>
          <div className="report-meta">Filter: {filterLabel}</div>
          <div className="report-meta">Generated: {generatedAt}</div>
          <div className="report-meta">{clients.length} client(s)</div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-label">Total Clients</div>
          <div className="stat-value" style={{ color: "#1c1917" }}>{stats.total}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">With Events</div>
          <div className="stat-value" style={{ color: "#7c3aed" }}>{stats.withEvents}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Total Billed</div>
          <div className="stat-value" style={{ color: "#16a34a" }}>{fmt(stats.totalRevenue)}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Outstanding Balance</div>
          <div className="stat-value" style={{ color: stats.totalBalance > 0 ? "#b45309" : "#16a34a" }}>{fmt(stats.totalBalance)}</div>
        </div>
      </div>

      {/* Table */}
      <table>
        <thead>
          <tr>
            <th style={{ width: 24 }}>#</th>
            <th>Client Name</th>
            <th>Company</th>
            <th>Phone</th>
            <th>Email</th>
            <th className="right">Events</th>
            <th className="right">Billed</th>
            <th className="right">Paid</th>
            <th className="right">Balance</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c, i) => (
            <tr key={c.id}>
              <td style={{ color: "#a8a29e" }}>{i + 1}</td>
              <td style={{ fontWeight: 600 }}>{c.name}</td>
              <td style={{ color: "#78716c" }}>{c.company_name || "—"}</td>
              <td>{c.phone || "—"}</td>
              <td style={{ color: "#78716c" }}>{c.email || "—"}</td>
              <td className="right">{c.events_count || 0}</td>
              <td className="right">{fmt(c.total_revenue || 0)}</td>
              <td className="right">{fmt(c.total_paid || 0)}</td>
              <td className={`right ${(c.balance || 0) > 0 ? "outstanding" : "cleared"}`}>
                {(c.balance || 0) > 0 ? fmt(c.balance || 0) : "Cleared"}
              </td>
            </tr>
          ))}
          <tr className="total-row">
            <td colSpan={5}>TOTALS ({clients.length} clients)</td>
            <td className="right">{clients.reduce((s, c) => s + (c.events_count || 0), 0)}</td>
            <td className="right">{fmt(clients.reduce((s, c) => s + (c.total_revenue || 0), 0))}</td>
            <td className="right">{fmt(clients.reduce((s, c) => s + (c.total_paid || 0), 0))}</td>
            <td className={`right ${stats.totalBalance > 0 ? "outstanding" : "cleared"}`}>
              {fmt(stats.totalBalance)}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="footer">
        This report was generated from the management system on {generatedAt}. Confidential — for internal use only.
      </div>
    </div>
  );
}
